import React, { useState, useRef, useEffect } from 'react';
import { Play, VolumeX, Volume2, Maximize, Minimize } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const ctrlBtn = {
  width: '48px',
  height: '48px',
  border: '1px solid #C9A84C',
  color: '#C9A84C',
  background: 'rgba(10,10,10,0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

export const GalleryVideo = () => {
  const { t } = useLanguage();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const videoRef = useRef(null);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) videoRef.current.pause();
    else videoRef.current.play();
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      const el = videoRef.current;
      if (el?.requestFullscreen) el.requestFullscreen();
      else if (el?.webkitRequestFullscreen) el.webkitRequestFullscreen();
      else if (el?.mozRequestFullScreen) el.mozRequestFullScreen();
      else if (el?.msRequestFullscreen) el.msRequestFullscreen();
    } else {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      else if (document.mozCancelFullScreen) document.mozCancelFullScreen();
      else if (document.msExitFullscreen) document.msExitFullscreen();
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      const fs = !!(document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement);
      setIsFullscreen(fs);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  return (
    <div className="mb-12" style={{ background: '#1C1C1C', border: '1px solid rgba(201,168,76,0.18)' }}>
      <div className="relative">
        <video
          ref={videoRef}
          className="w-full object-contain bg-black"
          style={{ aspectRatio: '16/9', height: '320px' }}
          muted={isMuted}
          onEnded={() => setIsPlaying(false)}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
        >
          <source src="https://customer-assets.emergentagent.com/job_ramen-fullstack/artifacts/f17a4x04_047039fd4c4446298f403f7d8ca7235c.mp4" type="video/mp4" />
          {t('gallery.videoNotSupported')}
        </video>

        <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(10,10,10,0.2)' }}>
          <div className="flex items-center gap-3">
            <button onClick={togglePlay} style={{ ...ctrlBtn, width: '56px', height: '56px', borderColor: '#C8282B', color: '#fff', background: 'rgba(200,40,43,0.85)' }} data-testid="gallery-video-play">
              {isPlaying ? (
                <div className="flex"><div style={{ width: '6px', height: '20px', background: '#fff', marginRight: '4px' }} /><div style={{ width: '6px', height: '20px', background: '#fff' }} /></div>
              ) : (
                <Play className="w-6 h-6 ml-1" />
              )}
            </button>
            <button onClick={toggleMute} style={ctrlBtn}>
              {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </button>
            <button onClick={toggleFullscreen} style={ctrlBtn}>
              {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <div className="absolute bottom-4 left-4 right-4">
          <div style={{ background: 'rgba(10,10,10,0.8)', padding: '16px', borderLeft: '2px solid #C9A84C' }}>
            <h3 className="font-display" style={{ fontSize: '22px', fontWeight: 700, color: '#F5F5F0' }}>{t('gallery.videoTitle')}</h3>
            <p className="font-body mt-1" style={{ fontSize: '14px', fontWeight: 300, color: '#C2C1B6' }}>{t('gallery.videoDescription')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};
